import type {
  GlobalFilters,
  ProcessAttribute,
  ProcessCatalogNode,
  ProcessCatalogUpload,
  ProcessCatalogView,
  ProcessChangeEvent,
  ProcessDetailView,
  ProcessEvidenceItem,
  ProcessLevel,
  ProcessLinkedProduct,
  ProcessValidationFinding,
  QueryMetadata,
  QueryResult,
} from '@aurora-pvi/contracts';
import type { RepositoryContext } from './context';
import { resolved } from './context';

/**
 * Aurora Airlines' L1–L4 process catalog, as the last accepted file feed left
 * it.
 *
 * There is no catalog endpoint yet, so this module stands in for one. It is
 * written as a pure builder over authored seeds rather than as a literal blob
 * for two reasons: the parent/child, descendant and roll-up relationships have
 * to be *derived* or they drift the first time a node moves, and a reviewer
 * should be able to see the taxonomy itself — six L1 domains and what hangs off
 * them — without reading seventy objects.
 *
 * Nothing here is random. Where a figure is synthesised it comes from a stable
 * hash of the process code, so the same catalog renders identically on every
 * reload and a screenshot taken today matches one taken last week.
 *
 * The codes match `seed/src/processes.ts` (`PRC-4.1.2` and friends) so this
 * catalog and the mapping studio are talking about the same airline.
 */

// ── People ───────────────────────────────────────────────────────────────────

interface Person {
  name: string;
  role: string;
}

const PEOPLE: Record<string, Person> = {
  raman: { name: 'Priya Raman', role: 'VP Network Planning' },
  lindqvist: { name: 'Marcus Lindqvist', role: 'Director, Revenue Management' },
  okonjo: { name: 'Dele Okonjo', role: 'Head of Flight Operations' },
  devries: { name: 'Anneke de Vries', role: 'VP Crew Resources' },
  nasser: { name: 'Ibrahim Nasser', role: 'Director, Ground Operations' },
  marchetti: { name: 'Sofia Marchetti', role: 'Head of Engineering Planning' },
  chen: { name: 'Wei Chen', role: 'Manager, Slot Coordination' },
  albright: { name: 'Rhona Albright', role: 'Manager, Pricing' },
  duarte: { name: 'Tomás Duarte', role: 'Manager, Order Platform' },
  keita: { name: 'Aminata Keïta', role: 'Manager, Operations Control' },
  novak: { name: 'Jan Novák', role: 'Manager, Crew Planning' },
  osei: { name: 'Grace Osei', role: 'Manager, Passenger Services' },
  // A role mailbox rather than a person. The catalog accepts it and warns:
  // an inbox cannot be accountable for a process at an audit.
  turnDesk: { name: 'turn.coordination@aurora.example', role: 'Shared role mailbox' },
};

// ── The taxonomy ─────────────────────────────────────────────────────────────

interface Seed {
  code: string;
  name: string;
  /** Key into `PEOPLE`. Declared at L1 and L2; deeper levels inherit. */
  owner?: keyof typeof PEOPLE;
  children?: Seed[];
}

const CATALOG: Seed[] = [
  {
    code: 'PRC-1',
    name: 'Plan the Network & Schedule',
    owner: 'raman',
    children: [
      {
        code: 'PRC-1.2',
        name: 'Build the Schedule',
        owner: 'raman',
        children: [
          {
            code: 'PRC-1.2.1',
            name: 'Draft Seasonal Schedule',
            children: [
              { code: 'PRC-1.2.1.1', name: 'Load demand forecast' },
              { code: 'PRC-1.2.1.2', name: 'Generate candidate schedules' },
            ],
          },
          {
            code: 'PRC-1.2.2',
            name: 'Assign Fleet to Schedule',
            children: [
              { code: 'PRC-1.2.2.1', name: 'Apply fleet assignment model' },
              { code: 'PRC-1.2.2.2', name: 'Reconcile maintenance opportunities' },
            ],
          },
          { code: 'PRC-1.2.3', name: 'Validate Schedule Feasibility' },
        ],
      },
      {
        code: 'PRC-1.3',
        name: 'Manage Slots & Airport Access',
        owner: 'chen',
        children: [
          { code: 'PRC-1.3.1', name: 'Submit Slot Requests' },
          { code: 'PRC-1.3.2', name: 'Monitor Slot Compliance' },
        ],
      },
    ],
  },
  {
    code: 'PRC-2',
    name: 'Sell & Distribute',
    owner: 'lindqvist',
    children: [
      {
        code: 'PRC-2.1',
        name: 'Manage Fares & Pricing',
        owner: 'albright',
        children: [
          {
            code: 'PRC-2.1.1',
            name: 'File Fares',
            children: [
              { code: 'PRC-2.1.1.1', name: 'Capture fare rules' },
              { code: 'PRC-2.1.1.2', name: 'Publish fares to the distribution filing agent' },
            ],
          },
          { code: 'PRC-2.1.2', name: 'Set Bid Prices' },
          { code: 'PRC-2.1.3', name: 'Approve Promotional Fares' },
        ],
      },
      {
        code: 'PRC-2.4',
        name: 'Create & Manage Orders',
        owner: 'duarte',
        children: [
          {
            code: 'PRC-2.4.1',
            name: 'Create Booking',
            children: [
              { code: 'PRC-2.4.1.1', name: 'Validate passenger details' },
              { code: 'PRC-2.4.1.2', name: 'Reserve inventory' },
            ],
          },
          {
            code: 'PRC-2.4.2',
            name: 'Take Payment',
            children: [
              { code: 'PRC-2.4.2.1', name: 'Authorise card payment' },
              { code: 'PRC-2.4.2.2', name: 'Handle 3-D Secure challenge' },
            ],
          },
          { code: 'PRC-2.4.3', name: 'Issue Ticket & Documents' },
        ],
      },
    ],
  },
  {
    code: 'PRC-4',
    name: 'Operate the Flight',
    owner: 'okonjo',
    children: [
      {
        code: 'PRC-4.1',
        name: 'Plan the Flight',
        owner: 'okonjo',
        children: [
          {
            code: 'PRC-4.1.1',
            name: 'Build Operational Flight Plan',
            children: [
              { code: 'PRC-4.1.1.1', name: 'Retrieve route and airspace constraints' },
              { code: 'PRC-4.1.1.2', name: 'Compute fuel burn profile' },
            ],
          },
          { code: 'PRC-4.1.2', name: 'Review Weather & NOTAMs' },
          { code: 'PRC-4.1.3', name: 'Issue Dispatch Release' },
        ],
      },
      {
        code: 'PRC-4.2',
        name: 'Compute Weight & Balance',
        owner: 'okonjo',
        children: [
          { code: 'PRC-4.2.1', name: 'Collect Load Data' },
          { code: 'PRC-4.2.2', name: 'Issue Loadsheet' },
        ],
      },
      {
        code: 'PRC-4.3',
        name: 'Control the Operation',
        owner: 'keita',
        children: [
          { code: 'PRC-4.3.1', name: 'Monitor Flight Progress' },
          {
            code: 'PRC-4.3.2',
            name: 'Decide Recovery Option',
            children: [
              { code: 'PRC-4.3.2.1', name: 'Generate recovery options' },
              { code: 'PRC-4.3.2.2', name: 'Select and publish the recovery option' },
            ],
          },
        ],
      },
    ],
  },
  {
    code: 'PRC-5',
    name: 'Manage Crew',
    owner: 'devries',
    children: [
      {
        code: 'PRC-5.2',
        name: 'Build Pairings & Rosters',
        owner: 'novak',
        children: [
          {
            code: 'PRC-5.2.1',
            name: 'Generate Pairings',
            children: [
              { code: 'PRC-5.2.1.1', name: 'Run pairing optimisation' },
              { code: 'PRC-5.2.1.2', name: 'Review pairing cost' },
            ],
          },
          { code: 'PRC-5.2.2', name: 'Run Legality Check' },
          { code: 'PRC-5.2.3', name: 'Publish Roster' },
        ],
      },
      {
        code: 'PRC-5.4',
        name: 'Recover Disrupted Crew',
        owner: 'devries',
        children: [
          { code: 'PRC-5.4.1', name: 'Identify Affected Pairings' },
          {
            code: 'PRC-5.4.2',
            name: 'Assign Replacement Crew',
            children: [
              { code: 'PRC-5.4.2.1', name: 'Check duty and rest limits' },
              { code: 'PRC-5.4.2.2', name: 'Notify the crew member' },
            ],
          },
        ],
      },
    ],
  },
  {
    code: 'PRC-6',
    name: 'Handle the Airport & Ground',
    owner: 'nasser',
    children: [
      {
        code: 'PRC-6.1',
        name: 'Accept Passengers',
        owner: 'osei',
        children: [
          { code: 'PRC-6.1.1', name: 'Verify Travel Documents' },
          {
            code: 'PRC-6.1.2',
            name: 'Accept Baggage',
            children: [
              { code: 'PRC-6.1.2.1', name: 'Weigh and tag the bag' },
              { code: 'PRC-6.1.2.2', name: 'Collect excess baggage fee' },
            ],
          },
        ],
      },
      {
        code: 'PRC-6.4',
        name: 'Turn the Aircraft',
        owner: 'turnDesk',
        children: [
          { code: 'PRC-6.4.1', name: 'Sequence Turn Tasks' },
          { code: 'PRC-6.4.2', name: 'Monitor Turn Milestones' },
          { code: 'PRC-6.4.3', name: 'Escalate Turn Delay' },
        ],
      },
    ],
  },
  {
    code: 'PRC-7',
    name: 'Maintain the Fleet',
    owner: 'marchetti',
    children: [
      {
        code: 'PRC-7.1',
        name: 'Plan Maintenance',
        owner: 'marchetti',
        children: [
          { code: 'PRC-7.1.1', name: 'Forecast Check Due' },
          { code: 'PRC-7.1.2', name: 'Plan Hangar Slot' },
        ],
      },
      {
        code: 'PRC-7.3',
        name: 'Manage Defects & Deferrals',
        owner: 'marchetti',
        children: [
          { code: 'PRC-7.3.1', name: 'Raise Defect' },
          { code: 'PRC-7.3.2', name: 'Apply MEL Deferral' },
        ],
      },
    ],
  },
];

// ── Validation findings ──────────────────────────────────────────────────────

/**
 * The rules that fired on the 15 July batch, by process code.
 *
 * Authored rather than generated: a validation reason is the one string on
 * these screens a user is expected to act on, and a template-generated reason
 * ("row failed rule 4") is exactly the kind of message that teaches people to
 * ignore the column.
 */
const FINDINGS: Record<string, ProcessValidationFinding> = {
  'PRC-1.3.2': {
    state: 'warning',
    ruleId: 'CAT-OWN-01',
    reason:
      'No accountable owner in the workbook — the Process Owner column was blank on this row. The step is published under the L2 owner until one is named.',
    sourceRow: 41,
  },
  'PRC-2.1.3': {
    state: 'error',
    ruleId: 'CAT-DUP-02',
    reason:
      'The code PRC-2.1.3 appears twice in the workbook with different names ("Approve Promotional Fares" and "Approve Tactical Fares"). Neither row was accepted; the published step is the one from the April batch.',
    sourceRow: 63,
  },
  'PRC-2.4.2.2': {
    state: 'warning',
    ruleId: 'CAT-EVD-04',
    reason:
      'No evidence attached. Every task inside a payment-handling process must carry a process map or control narrative before the PCI DSS attestation in October.',
    sourceRow: 88,
  },
  'PRC-4.1.2': {
    state: 'warning',
    ruleId: 'CAT-OWN-03',
    reason:
      'Two rows in the workbook give different owners for this code. The published owner is the one already in the catalog; the second value was not merged.',
    sourceRow: 104,
  },
  'PRC-4.3.2.2': {
    state: 'error',
    ruleId: 'CAT-REF-07',
    reason:
      'The owner "ops.recovery@aurora.example" does not resolve to a registered person or organisational unit, so the row could not be attributed and was rejected.',
    sourceRow: 131,
  },
  'PRC-5.2.2': {
    state: 'warning',
    ruleId: 'CAT-DEC-05',
    reason:
      'No L4 tasks supplied. Crew steps are expected to decompose to task level because duty-limit checks are audited at that granularity.',
    sourceRow: 146,
  },
  'PRC-6.1.2.2': {
    state: 'error',
    ruleId: 'CAT-DUP-03',
    reason:
      'Defines the same task as PRC-2.6.2 "Capture Ancillary Purchase" — same name, same system of record, two branches. The catalog cannot hold one task in two places; both rows are held out pending a decision.',
    sourceRow: 168,
  },
  'PRC-6.4.3': {
    state: 'warning',
    ruleId: 'CAT-OWN-02',
    reason:
      'The owner is a shared role mailbox rather than a named person. Accepted, but an inbox cannot be accountable at an audit.',
    sourceRow: 174,
  },
  'PRC-7.1.2': {
    state: 'warning',
    ruleId: 'CAT-MAP-06',
    reason:
      'No product is mapped to this step, so its run cost cannot be attributed to any product line.',
    sourceRow: 189,
  },
  'PRC-7.3.2': {
    state: 'error',
    ruleId: 'CAT-EFF-08',
    reason:
      'Effective-from date 2027-01-01 is later than the catalog as-of date, so the row describes a process that does not yet exist. It was not accepted.',
    sourceRow: 194,
  },
};

const VALID: ProcessValidationFinding = {
  state: 'valid',
  ruleId: null,
  reason: null,
  sourceRow: null,
};

// ── Deterministic synthesis ──────────────────────────────────────────────────

/** FNV-1a. Small, stable, and dependency-free — the same code always hashes the same. */
function hash(value: string): number {
  let h = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    h ^= value.charCodeAt(index);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/** `PRC-4.1.2` → `prc-4-1-2`. Stable, and safe in a URL path segment. */
function idFor(code: string): string {
  return code.toLowerCase().replace(/\./g, '-');
}

function levelFor(code: string): ProcessLevel {
  const depth = code.split('.').length;
  return depth === 1 ? 'L1' : depth === 2 ? 'L2' : depth === 3 ? 'L3' : 'L4';
}

// ── Products ─────────────────────────────────────────────────────────────────

interface ProductSeed {
  code: string;
  name: string;
}

/**
 * The products each domain's steps map to. Domain-scoped rather than a single
 * pool, so a synthesised mapping is at least plausible — a fare-filing step
 * never comes back mapped to Baggage Services.
 */
const PRODUCTS_BY_DOMAIN: Record<string, ProductSeed[]> = {
  'PRC-1': [
    { code: 'PRD-005', name: 'Schedule Management' },
    { code: 'PRD-041', name: 'Network Planning Analytics' },
    { code: 'PRD-063', name: 'Slot & Airport Access' },
  ],
  'PRC-2': [
    { code: 'PRD-011', name: 'Revenue Management' },
    { code: 'PRD-007', name: 'Offer & Order Platform' },
    { code: 'PRD-018', name: 'Payments & Settlement' },
  ],
  'PRC-4': [
    { code: 'PRD-021', name: 'Flight Planning Suite' },
    { code: 'PRD-029', name: 'Disruption Recovery' },
    { code: 'PRD-036', name: 'Load Control' },
  ],
  'PRC-5': [
    { code: 'PRD-014', name: 'Crew Rostering Platform' },
    { code: 'PRD-048', name: 'Crew Tracking' },
  ],
  'PRC-6': [
    { code: 'PRD-032', name: 'Baggage Services' },
    { code: 'PRD-045', name: 'Turn Coordination' },
    { code: 'PRD-057', name: 'Departure Control' },
  ],
  'PRC-7': [
    { code: 'PRD-052', name: 'Maintenance Planning' },
    { code: 'PRD-068', name: 'Technical Records' },
  ],
};

function domainOf(code: string): string {
  const [head] = code.split('.');
  return head ?? code;
}

/**
 * How many products the published mapping set links to an L3 step.
 *
 * Zero is a genuine answer here — an unmapped step — and one of the catalog's
 * warnings is raised on exactly that. It is not a missing value, so the screen
 * renders it as "None mapped" rather than as a bare 0 in a numeric column.
 */
function mappedProductsFor(code: string): ProductSeed[] {
  const pool = PRODUCTS_BY_DOMAIN[domainOf(code)] ?? [];
  if (pool.length === 0) return [];
  if (code === 'PRC-7.1.2') return []; // the CAT-MAP-06 warning above.
  const count = hash(code) % 3;
  const start = hash(`${code}:start`) % pool.length;
  const picked: ProductSeed[] = [];
  for (let index = 0; index < count; index += 1) {
    const product = pool[(start + index) % pool.length];
    if (product) picked.push(product);
  }
  return picked;
}

// ── Build ────────────────────────────────────────────────────────────────────

const SOURCE_SYSTEM = 'FEED:PROCESS-CATALOG';

interface Built {
  nodes: ProcessCatalogNode[];
  byId: Map<string, ProcessCatalogNode>;
}

function build(): Built {
  const nodes: ProcessCatalogNode[] = [];
  const byId = new Map<string, ProcessCatalogNode>();
  let recordIndex = 0;

  const walk = (seed: Seed, parent: ProcessCatalogNode | null, inheritedOwner: Person | null) => {
    recordIndex += 1;
    const level = levelFor(seed.code);
    const own = seed.owner ? (PEOPLE[seed.owner] ?? null) : null;
    const owner = own ?? inheritedOwner;
    const products = level === 'L3' ? mappedProductsFor(seed.code) : [];

    const node: ProcessCatalogNode = {
      id: idFor(seed.code),
      code: seed.code,
      name: seed.name,
      level,
      parentId: parent?.id ?? null,
      childIds: [],
      descendantCount: 0,
      ownerName: own?.name ?? null,
      ownerRole: own?.role ?? null,
      ownerMissingReason: own
        ? null
        : owner
          ? // Not a gap: the feed records accountability at L1 and L2 and the
            // taxonomy inherits it downwards. Saying so is the difference
            // between "nobody owns this" and "the owner is one level up".
            `Accountability is recorded at L1 and L2. This step runs under ${owner.name}, the owner of ${parent?.code ?? 'its parent'}.`
          : 'No owner is recorded anywhere on this branch of the catalog.',
      linkedProductCount: level === 'L4' ? null : level === 'L3' ? products.length : 0,
      linkedProductMissingReason:
        level === 'L4'
          ? 'Products are mapped at L3 and above. An L4 task is delivered by whatever product owns its parent step.'
          : null,
      validation: FINDINGS[seed.code] ?? VALID,
      descendantErrorCount: 0,
      descendantWarningCount: 0,
      confidence: level === 'L1' || level === 'L2' ? 'High' : level === 'L3' ? 'Medium' : 'Low',
      sourceSystem: SOURCE_SYSTEM,
      sourceRecordId: `CAT-${String(recordIndex).padStart(5, '0')}`,
      // Deeper rows churn: the taxonomy's top is quarterly, its tasks change
      // whenever a team changes how it works.
      sourceUpdatedAt:
        level === 'L1' || level === 'L2'
          ? '2026-04-02T05:40:00.000Z'
          : level === 'L3'
            ? '2026-07-15T06:12:00.000Z'
            : '2026-07-15T06:12:00.000Z',
    };

    nodes.push(node);
    byId.set(node.id, node);
    if (parent) parent.childIds.push(node.id);

    for (const child of seed.children ?? []) walk(child, node, owner);
  };

  for (const root of CATALOG) walk(root, null, null);

  // Second pass, deepest first: descendant counts and the validation roll-up.
  // Iterating the array in reverse works because `walk` is depth-first, so
  // every child is appended after its parent.
  for (let index = nodes.length - 1; index >= 0; index -= 1) {
    const node = nodes[index];
    if (!node) continue;
    for (const childId of node.childIds) {
      const child = byId.get(childId);
      if (!child) continue;
      node.descendantCount += 1 + child.descendantCount;
      node.descendantErrorCount +=
        child.descendantErrorCount + (child.validation.state === 'error' ? 1 : 0);
      node.descendantWarningCount +=
        child.descendantWarningCount + (child.validation.state === 'warning' ? 1 : 0);
    }
    // L1 and L2 roll their children's product counts up; the leaves already
    // carry theirs. Counting L3 links only means an application shared by two
    // steps is not counted twice at the domain.
    if (node.level === 'L1' || node.level === 'L2') {
      let total = 0;
      for (const childId of node.childIds) {
        const child = byId.get(childId);
        total += child?.linkedProductCount ?? 0;
      }
      node.linkedProductCount = total;
    }
  }

  return { nodes, byId };
}

const BUILT = build();

// ── Uploads ──────────────────────────────────────────────────────────────────

const CURRENT_UPLOAD: ProcessCatalogUpload = {
  id: 'CAT-2026-07-15',
  fileName: 'aurora-process-catalog-v4.2.xlsx',
  format: 'XLSX',
  receivedAt: '2026-07-15T06:12:00.000Z',
  receivedBy: 'Helena Voss — Data Steward, Enterprise Architecture',
  sourceSystem: SOURCE_SYSTEM,
  rowsRead: 78,
  rowsAccepted: BUILT.nodes.length,
  rowsWarned: 6,
  rowsRejected: 4,
  rowsUnchanged: 3,
  state: 'Validated',
  exceptionWorkbookAvailable: false,
  exceptionWorkbookReason:
    'The ten findings below are every row the exception workbook would contain. Connect an ingestion feed to download it as a file.',
};

const UPLOAD_HISTORY: ProcessCatalogUpload[] = [
  {
    id: 'CAT-2026-04-02',
    fileName: 'aurora-process-catalog-v4.1.xlsx',
    format: 'XLSX',
    receivedAt: '2026-04-02T05:40:00.000Z',
    receivedBy: 'Helena Voss — Data Steward, Enterprise Architecture',
    sourceSystem: SOURCE_SYSTEM,
    rowsRead: 74,
    rowsAccepted: 71,
    rowsWarned: 3,
    rowsRejected: 3,
    rowsUnchanged: 0,
    state: 'Validated',
    exceptionWorkbookAvailable: false,
    exceptionWorkbookReason: 'Exception workbooks are retained for 30 days. This batch is older.',
  },
  {
    id: 'CAT-2026-03-28',
    fileName: 'process-catalog-draft.csv',
    format: 'CSV',
    receivedAt: '2026-03-28T14:03:00.000Z',
    receivedBy: 'Helena Voss — Data Steward, Enterprise Architecture',
    sourceSystem: SOURCE_SYSTEM,
    rowsRead: 74,
    rowsAccepted: 0,
    rowsWarned: 0,
    rowsRejected: 74,
    rowsUnchanged: 0,
    state: 'Rejected',
    exceptionWorkbookAvailable: false,
    exceptionWorkbookReason:
      'The whole batch was rejected before row validation: the file had no Level column, so no row could be placed in the hierarchy.',
  },
];

// ── The catalog view ─────────────────────────────────────────────────────────

function processCatalogView(): ProcessCatalogView {
  const levelCounts: Record<ProcessLevel, number> = { L1: 0, L2: 0, L3: 0, L4: 0 };
  for (const node of BUILT.nodes) levelCounts[node.level] += 1;

  return {
    nodes: BUILT.nodes,
    upload: CURRENT_UPLOAD,
    uploadHistory: UPLOAD_HISTORY,
    provenanceNote: `The catalogue was last delivered by ${CURRENT_UPLOAD.sourceSystem}.`,
    levelCounts,
  };
}

/**
 * The query envelope the screens read as attentively as the payload.
 *
 * `asOf` is threaded from the global filters: choosing a past date puts the
 * screen into its read-only historical state rather than silently showing
 * today's catalog under yesterday's date.
 */
function processQueryMeta(asOf: string | null): QueryMetadata {
  return {
    correlationId: 'fixture-process-catalog',
    periodStart: '2026-01-01',
    periodEnd: '2026-12-31',
    comparisonPeriodStart: null,
    comparisonPeriodEnd: null,
    asOf,
    sourceStatus: {
      overall: 'Available',
      refreshedAt: CURRENT_UPLOAD.receivedAt,
      lastGoodAt: CURRENT_UPLOAD.receivedAt,
      failedRunAt: null,
      contributingSources: [
        {
          sourceSystem: SOURCE_SYSTEM,
          status: 'Available',
          refreshedAt: CURRENT_UPLOAD.receivedAt,
          recordCount: BUILT.nodes.length,
        },
        {
          sourceSystem: 'AURORA-MAPPING-SET',
          status: 'Available',
          refreshedAt: '2026-07-20T02:15:00.000Z',
          recordCount: 118,
        },
      ],
    },
    quality: {
      score: 86,
      completeness: 0.91,
      validity: 0.95,
      uniqueness: 0.97,
      consistency: 0.88,
      timeliness: 0.74,
      openIssueCount: 10,
    },
    confidence: 'Medium',
    scope: {
      scopeId: '00000000-0000-4000-8000-000000000001',
      scopeType: 'Enterprise',
      scopeLabel: 'Aurora Airlines — enterprise',
      isNarrowed: false,
      maskedFields: [],
    },
    allowedActions: [
      {
        action: 'process.uploadCatalog',
        permission: 'administer',
        enabled: false,
        disabledReason:
          'Catalogue changes are submitted through the intake form, where they are validated before they reach the catalogue.',
      },
      {
        action: 'process.editAttributes',
        permission: 'edit',
        enabled: false,
        disabledReason:
          'Process attributes arrive by file feed. Editing them in place would be overwritten by the next batch.',
      },
      {
        action: 'process.attachEvidence',
        permission: 'edit',
        enabled: false,
        disabledReason: 'Evidence is attached through the governance workbench, not this screen.',
      },
    ],
    isSyntheticData: true,
  };
}

// ── Detail ───────────────────────────────────────────────────────────────────

/** Per-domain colour for the attributes a catalog feed does not itself carry. */
interface DomainFacts {
  criticality: string;
  frequency: string;
  systems: string;
  regulatory: string | null;
}

const DOMAIN_FACTS: Record<string, DomainFacts> = {
  'PRC-1': {
    criticality: 'Business critical',
    frequency: 'Seasonal, with a weekly change cycle',
    systems: 'Schedule Manager, Fleet Assignment Engine',
    regulatory: 'IATA Worldwide Slot Guidelines — slot compliance is externally audited.',
  },
  'PRC-2': {
    criticality: 'Mission critical',
    frequency: 'Continuous — every transaction',
    systems: 'Offer & Order Platform, Fare Filing Gateway, Payment Gateway',
    regulatory: 'PCI DSS v4.0 — any step that touches cardholder data is in scope.',
  },
  'PRC-4': {
    criticality: 'Mission critical',
    frequency: 'Per flight — roughly 640 times a day',
    systems: 'Flight Planning Suite, Operations Control System, Load Control',
    regulatory: 'EASA Part-ORO — operational control and dispatch are certified activities.',
  },
  'PRC-5': {
    criticality: 'Mission critical',
    frequency: 'Monthly roster cycle, with daily recovery',
    systems: 'Crew Rostering Platform, Crew Tracking',
    regulatory: 'EASA Flight Time Limitations (ORO.FTL) — duty and rest are legally bounded.',
  },
  'PRC-6': {
    criticality: 'Business critical',
    frequency: 'Per departure — roughly 640 times a day',
    systems: 'Departure Control, Baggage Reconciliation, Turn Coordination',
    regulatory: 'Annex 17 security screening obligations apply to document verification.',
  },
  'PRC-7': {
    criticality: 'Mission critical',
    frequency: 'Per check, per defect',
    systems: 'Maintenance Planning, Technical Records',
    regulatory:
      'EASA Part-M continuing airworthiness — records are retained for the aircraft life.',
  },
};

/** Evidence exists for the processes an auditor has actually asked about. */
const EVIDENCE: Record<string, ProcessEvidenceItem[]> = {
  'PRC-4.1': [
    {
      id: 'evd-4-1-a',
      kind: 'Process map',
      title: 'Flight planning — end-to-end map, v6',
      sourceSystem: 'Governance workbench',
      capturedAt: '2026-05-11T09:20:00.000Z',
      state: 'Verified',
      note: 'Accepted by the Flight Operations process council on 11 May 2026.',
    },
    {
      id: 'evd-4-1-b',
      kind: 'Control narrative',
      title: 'Dispatch release authority and delegation',
      sourceSystem: 'Governance workbench',
      capturedAt: '2026-02-18T11:05:00.000Z',
      state: 'Expired',
      note: 'Reviewed annually. This copy lapsed on 18 February 2026 and has not been replaced.',
    },
  ],
  'PRC-5.2': [
    {
      id: 'evd-5-2-a',
      kind: 'SOP',
      title: 'Roster publication and bid resolution, SOP-CRW-014',
      sourceSystem: 'Document management',
      capturedAt: '2026-06-30T08:00:00.000Z',
      state: 'Verified',
      note: null,
    },
    {
      id: 'evd-5-2-b',
      kind: 'Regulatory reference',
      title: 'ORO.FTL legality rule set applied to pairing generation',
      sourceSystem: 'Compliance register',
      capturedAt: '2026-01-09T16:42:00.000Z',
      state: 'Unverified',
      note: 'Uploaded by crew planning; not yet countersigned by compliance.',
    },
  ],
  'PRC-2.4.2': [
    {
      id: 'evd-2-4-2-a',
      kind: 'Control narrative',
      title: 'Card authorisation and settlement controls (PCI DSS 4.0)',
      sourceSystem: 'Compliance register',
      capturedAt: '2026-04-22T13:30:00.000Z',
      state: 'Verified',
      note: 'Covers the parent step. The 3-D Secure task below it is not covered — see its warning.',
    },
  ],
  'PRC-6.4': [
    {
      id: 'evd-6-4-a',
      kind: 'Process map',
      title: 'Aircraft turn — 45 minute standard, narrowbody',
      sourceSystem: 'Governance workbench',
      capturedAt: '2025-11-14T10:15:00.000Z',
      state: 'Unverified',
      note: 'Predates the current turn target of 40 minutes; ground operations have flagged it.',
    },
  ],
};

/** Changes beyond the batch load itself. Authored, because each has a reason. */
const CHANGES: Record<string, ProcessChangeEvent[]> = {
  'PRC-6.4': [
    {
      id: 'chg-6-4-a',
      at: '2026-07-15T06:12:00.000Z',
      actor: 'Catalog feed (batch CAT-2026-07-15)',
      field: 'Accountable owner',
      from: 'Ibrahim Nasser',
      to: 'turn.coordination@aurora.example',
      reason:
        'Ground operations moved turn accountability to the coordination desk during the summer restructure.',
      batchId: 'CAT-2026-07-15',
    },
  ],
  'PRC-4.1.2': [
    {
      id: 'chg-4-1-2-a',
      at: '2026-07-15T06:12:00.000Z',
      actor: 'Catalog feed (batch CAT-2026-07-15)',
      field: 'Accountable owner',
      from: 'Dele Okonjo',
      to: null,
      reason:
        'Two rows in the batch proposed different owners. Neither was applied; the existing value stands.',
      batchId: 'CAT-2026-07-15',
    },
  ],
  'PRC-5.2': [
    {
      id: 'chg-5-2-a',
      at: '2026-04-02T05:40:00.000Z',
      actor: 'Catalog feed (batch CAT-2026-04-02)',
      field: 'Name',
      from: 'Build Rosters',
      to: 'Build Pairings & Rosters',
      reason:
        'Pairing construction was previously implicit in rostering; the process council split it out so pairing cost could be reported separately.',
      batchId: 'CAT-2026-04-02',
    },
    {
      id: 'chg-5-2-b',
      at: '2026-04-02T05:40:00.000Z',
      actor: 'Catalog feed (batch CAT-2026-04-02)',
      field: 'Accountable owner',
      from: 'Anneke de Vries',
      to: 'Jan Novák',
      reason: 'Delegated to crew planning when the L2 was split.',
      batchId: 'CAT-2026-04-02',
    },
  ],
};

function attributesFor(
  node: ProcessCatalogNode,
  parent: ProcessCatalogNode | null,
): ProcessAttribute[] {
  const facts = DOMAIN_FACTS[domainOf(node.code)];
  const attribute = (
    label: string,
    value: string | null,
    missingReason: string | null,
    sourceNote: string | null = null,
  ): ProcessAttribute => ({ label, value, missingReason, sourceNote });

  return [
    attribute('Process code', node.code, null),
    attribute(
      'Level',
      `${node.level} — ${
        node.level === 'L1'
          ? 'domain'
          : node.level === 'L2'
            ? 'process group'
            : node.level === 'L3'
              ? 'step'
              : 'task'
      }`,
      null,
    ),
    attribute(
      'Parent',
      parent ? `${parent.code} · ${parent.name}` : null,
      parent ? null : 'Top of the taxonomy. An L1 domain has no parent by design.',
    ),
    attribute(
      'Accountable owner',
      node.ownerName ? `${node.ownerName}${node.ownerRole ? ` · ${node.ownerRole}` : ''}` : null,
      node.ownerMissingReason,
    ),
    attribute(
      'Operating domain',
      `${domainOf(node.code)} · ${BUILT.byId.get(idFor(domainOf(node.code)))?.name ?? 'Unknown'}`,
      null,
    ),
    attribute(
      'Criticality',
      facts?.criticality ?? null,
      facts ? null : 'No criticality is registered for this domain.',
      'From the operational resilience register, not the catalog feed.',
    ),
    attribute(
      'Execution frequency',
      facts?.frequency ?? null,
      facts ? null : 'No frequency is registered for this domain.',
      'Recorded at domain level. Individual steps are not separately timed.',
    ),
    attribute(
      'Annual volume',
      null,
      'The catalog feed carries structure, not throughput. Volumes come from the operations data mart, which does not yet publish at process-code granularity.',
    ),
    attribute(
      'Systems of record',
      facts?.systems ?? null,
      facts ? null : 'No systems are registered for this domain.',
      'Derived from the published application-to-process mapping set.',
    ),
    attribute(
      'Effective from',
      '2026-04-01',
      null,
      'The date the current taxonomy version took effect.',
    ),
    attribute(
      'Last reviewed',
      node.level === 'L1' || node.level === 'L2' ? '2026-03-05' : null,
      node.level === 'L1' || node.level === 'L2'
        ? null
        : 'Review is recorded at L2 and above. This step was last examined as part of its parent group.',
      'March process council.',
    ),
    attribute(
      'Review cadence',
      node.level === 'L1' || node.level === 'L2' ? 'Annual, at the March process council' : null,
      node.level === 'L1' || node.level === 'L2'
        ? null
        : `Cadence is set on the parent group${parent ? ` (${parent.code})` : ''} and inherited.`,
    ),
    attribute('Data classification', 'Internal', null),
    attribute(
      'Regulatory relevance',
      facts?.regulatory ?? null,
      facts?.regulatory
        ? null
        : 'None recorded. Regulatory tagging is maintained by compliance in the governance workbench, and an absence here is not evidence that none applies.',
    ),
  ];
}

function linkedProductsFor(node: ProcessCatalogNode): ProcessLinkedProduct[] {
  if (node.level !== 'L3') return [];
  return mappedProductsFor(node.code).map((product, index) => ({
    id: `${node.id}-${product.code.toLowerCase()}`,
    code: product.code,
    name: product.name,
    role: index === 0 ? 'Primary' : 'Supporting',
    // The mapping set carries an allocation only where a steward set one. A
    // fabricated split would be indistinguishable from a governed one.
    allocationPercent:
      index === 0 ? 0.7 : hash(`${node.code}${product.code}`) % 2 === 0 ? 0.3 : null,
    allocationMissingReason:
      index === 0 || hash(`${node.code}${product.code}`) % 2 === 0
        ? null
        : 'No allocation was set on this relationship, so none of the step’s cost is attributed to this product.',
  }));
}

function historyFor(node: ProcessCatalogNode): ProcessChangeEvent[] {
  const authored = CHANGES[node.code] ?? [];
  // Every node has at least the moment it entered the catalog. Without it a
  // detail page with no edits reads as "no history recorded", which is a
  // different and wronger claim than "loaded, then never changed".
  const created: ProcessChangeEvent = {
    id: `${node.id}-created`,
    at: '2026-03-02T09:00:00.000Z',
    actor: 'Catalog feed (initial load)',
    field: 'Record',
    from: null,
    to: `${node.code} · ${node.name}`,
    reason: 'First load of the L1–L4 taxonomy from the enterprise architecture workbook.',
    batchId: 'CAT-2026-03-02',
  };
  return [...authored, created];
}

/** Null when the id is not in the catalog — the caller renders that, not a blank page. */
function processDetailView(processId: string | undefined): ProcessDetailView | null {
  if (!processId) return null;
  const node = BUILT.byId.get(processId);
  if (!node) return null;

  const ancestors: ProcessCatalogNode[] = [];
  let cursor = node.parentId ? BUILT.byId.get(node.parentId) : undefined;
  while (cursor) {
    ancestors.unshift(cursor);
    cursor = cursor.parentId ? BUILT.byId.get(cursor.parentId) : undefined;
  }

  const parent = ancestors.length > 0 ? (ancestors[ancestors.length - 1] ?? null) : null;
  const children = node.childIds
    .map((id) => BUILT.byId.get(id))
    .filter((child): child is ProcessCatalogNode => Boolean(child));

  const evidence = EVIDENCE[node.code] ?? [];

  return {
    node,
    ancestors,
    children,
    attributes: attributesFor(node, parent),
    linkedProducts: linkedProductsFor(node),
    linkedProductsNote:
      node.level === 'L4'
        ? 'Products are mapped at L3 and above. This task is delivered by whichever products own its parent step.'
        : node.level === 'L3'
          ? 'From the published mapping set, not from the catalog feed. Proposing a change is done in the mapping studio.'
          : 'Rolled up from the steps beneath this node. Mapping is done at step level, so nothing is mapped to a group directly.',
    evidence,
    evidenceNote:
      evidence.length > 0
        ? 'Evidence is held in the governance workbench. What is shown is the register entry, not the document.'
        : 'No evidence is registered against this process. The catalog feed carries structure, not documents — an empty register means nobody has attached one, not that none exists.',
    changeHistory: historyFor(node),
  };
}

// ── Repository ───────────────────────────────────────────────────────────────

export interface ProcessRepository {
  getCatalog(filters: GlobalFilters): Promise<QueryResult<ProcessCatalogView>>;
  /** Null data when the id is not in the catalog — the caller renders that. */
  getDetail(
    processId: string | undefined,
    filters: GlobalFilters,
  ): Promise<QueryResult<ProcessDetailView | null>>;
}

/**
 * The catalog and one process, behind the same envelope every other screen gets.
 *
 * `filters` reaches both calls only for `asOf`: choosing a past date puts these
 * screens into their read-only historical state, and the envelope is where that
 * decision is carried. The catalog itself is enterprise-wide and is not narrowed
 * by the caller's span — a process nobody in your scope performs is still part
 * of the taxonomy, and hiding it would make the level counts disagree between
 * two readers looking at the same published batch.
 */
export function createProcessRepository(_context: RepositoryContext): ProcessRepository {
  return {
    getCatalog(filters: GlobalFilters) {
      return resolved({
        data: processCatalogView(),
        meta: processQueryMeta(filters.asOf ?? null),
      });
    },
    getDetail(processId: string | undefined, filters: GlobalFilters) {
      return resolved({
        data: processDetailView(processId),
        meta: processQueryMeta(filters.asOf ?? null),
      });
    },
  };
}
